import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Layers, Compass, GitBranch, ArrowUpRight } from "lucide-react";
import { DOWNLOAD_URL } from "../../data/content";
import { Reveal, TiltCard, MagneticButton, scrollToId } from "./primitives";
import { useLanguage } from "../../context/LanguageContext";

const ICONS = { Layers, Compass, GitBranch };

function SectionLabel({ children, index }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className="font-mono-tech text-[11px] text-primary tracking-[0.25em]">{index}</span>
      <span className="w-6 h-px bg-white/20" />
      <span className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-white/50">{children}</span>
    </div>
  );
}

/* SECTION 2 — What is Berry */
export function AboutSection() {
  const { t } = useLanguage();

  return (
    <section id="about" data-testid="about-section" className="relative py-14 sm:py-20 md:py-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <SectionLabel index="[ 01 ]">{t.about.label}</SectionLabel>
            <Reveal as="h2" className="font-display font-bold tracking-tighter text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              {t.about.heading1} <br /> {t.about.heading2} <span className="text-primary">{t.about.headingAccent}</span>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1} className="text-white/60 text-sm sm:text-base md:text-lg leading-relaxed">
              {t.about.subcopy}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* SECTION 3 — Mod Ecosystem */
export function EcosystemSection() {
  const { t, lang } = useLanguage();

  return (
    <section id="ecosystem" data-testid="ecosystem-section" className="relative py-14 sm:py-20 md:py-32 bg-[#060608] border-y border-white/5">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="max-w-2xl mb-10 sm:mb-16">
          <SectionLabel index="[ 02 ]">{t.ecosystem.label}</SectionLabel>
          <Reveal as="h2" className="font-display font-bold tracking-tighter text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            {t.ecosystem.heading1} <br /> {t.ecosystem.heading2} <span className="text-primary">{t.ecosystem.headingAccent}</span>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {t.ecosystem.categories.map((c, i) => (
            <Reveal key={c.id} delay={(i % 3) * 0.08} data-testid={`category-card-${c.id}`}>
              <TiltCard
                intensity={7}
                onClick={() => c.url && scrollToId(c.url)}
                className="group relative h-44 sm:h-52 md:h-60 border border-white/8 bg-card overflow-hidden cursor-pointer"
              >
                <div className={`absolute inset-0 bg-gradient-to-t ${c.accent} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                {/* abstract 3d visual */}
                <div className="absolute -right-8 -top-8 w-40 h-40 border border-white/5 rounded-full group-hover:scale-110 transition-transform duration-700" style={{ transform: "translateZ(30px)" }} />
                <div className="absolute -right-2 top-6 w-24 h-24 border border-primary/20 rounded-full group-hover:border-primary/50 transition-colors duration-500" />
                <div className="relative h-full flex flex-col justify-end p-5 sm:p-6" style={{ transform: "translateZ(40px)" }}>
                  <div>
                    <h3 className="font-display font-semibold text-xl sm:text-2xl md:text-3xl text-white tracking-tight mb-1.5 group-hover:text-primary transition-colors duration-300">
                      {c.name}
                    </h3>
                    <p className="text-white/45 text-xs sm:text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </div>
                <ArrowUpRight size={18} className="absolute top-5 right-5 sm:top-6 sm:right-6 text-white/30 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* SECTION 5 — Why Berry */
export function WhySection() {
  const { t } = useLanguage();

  return (
    <section id="why" data-testid="why-section" className="relative py-14 sm:py-20 md:py-32 bg-[#060608] border-y border-white/5">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="mb-10 sm:mb-16 md:mb-24">
          <SectionLabel index="[ 04 ]">{t.why.label}</SectionLabel>
          <Reveal as="h2" className="font-display font-bold tracking-tighter text-white text-3xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.98]">
            {t.why.heading1} <span className="text-primary">{t.why.headingAccent}</span>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
          {t.why.principles.map((p, i) => (
            <Reveal
              key={p.num}
              delay={i * 0.09}
              data-testid={`principle-${p.num}`}
              className="group bg-[#060608] p-6 sm:p-8 md:p-10 hover:bg-card transition-colors duration-500"
            >
              <span className="block font-display font-bold text-4xl sm:text-5xl md:text-6xl text-white/10 group-hover:text-primary/70 transition-colors duration-500 mb-5 sm:mb-8">
                {p.num}
              </span>
              <h3 className="font-display font-semibold text-lg sm:text-xl text-white tracking-tight mb-2 sm:mb-3 uppercase">{p.title}</h3>
              <p className="text-white/45 text-xs sm:text-sm leading-relaxed">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* SECTION 6 — Brand Statement */
export function StatementSection() {
  const { t } = useLanguage();
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x1 = useTransform(scrollYProgress, [0, 1], [-80, 40]);
  const x2 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const x3 = useTransform(scrollYProgress, [0, 1], [-40, 80]);

  return (
    <section ref={ref} data-testid="statement-section" className="relative py-16 sm:py-24 md:py-40 overflow-hidden">
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[700px] max-h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(59,91,255,0.12), transparent 60%)" }}
      />
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <motion.h2
          style={{ x: x1 }}
          className="font-display font-bold tracking-tighter text-white text-4xl xs:text-5xl sm:text-6xl md:text-7xl lg:text-[7.5vw] xl:text-[8.5rem] leading-[0.92] whitespace-nowrap"
        >
          {t.statement.line1}
        </motion.h2>
        <motion.h2
          style={{ x: x2 }}
          className="font-display font-bold tracking-tighter text-white/25 text-4xl xs:text-5xl sm:text-6xl md:text-7xl lg:text-[7.5vw] xl:text-[8.5rem] leading-[0.92] text-right whitespace-nowrap"
        >
          {t.statement.line2}
        </motion.h2>
        <motion.h2
          style={{ x: x3 }}
          className="font-display font-bold tracking-tighter text-primary text-4xl xs:text-5xl sm:text-6xl md:text-7xl lg:text-[7.5vw] xl:text-[8.5rem] leading-[0.92] whitespace-nowrap"
        >
          {t.statement.line3}
        </motion.h2>
      </div>
    </section>
  );
}

/* FINAL CTA */
export function FinalCTA() {
  const { t } = useLanguage();

  return (
    <section data-testid="final-cta-section" className="relative py-16 sm:py-24 md:py-36 border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 tech-grid radial-fade opacity-30 pointer-events-none" />
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[90vw] h-[50vh] pointer-events-none"
        style={{ background: "radial-gradient(ellipse at bottom, rgba(59,91,255,0.18), transparent 65%)" }}
      />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 md:px-10 text-center">
        <Reveal as="h2" className="font-display font-bold tracking-tighter text-white text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
          {t.finalCta.heading1} <br /> {t.finalCta.heading2} <span className="text-primary">{t.finalCta.headingAccent}</span>
        </Reveal>
        <Reveal delay={0.15} className="mt-5 sm:mt-7 text-white/55 text-sm sm:text-base md:text-lg max-w-xl mx-auto">
          {t.finalCta.subcopy}
        </Reveal>
        <Reveal delay={0.3} className="mt-7 sm:mt-10 flex justify-center">
          <MagneticButton
            testId="final-cta-button"
            onClick={() => scrollToId(DOWNLOAD_URL)}
            className="btn-shine group inline-flex items-center justify-center gap-2.5 px-7 py-4 sm:px-9 sm:py-5 bg-primary text-white font-mono-tech text-[11px] sm:text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors duration-300 w-full sm:w-auto"
          >
            {t.finalCta.button}
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </MagneticButton>
        </Reveal>
      </div>
    </section>
  );
}
